/**
 * Upselling is hidden for now: its menu entries, its pages' addresses, the
 * «Ceder» action and its marks and filter in the deliveries history. Users did
 * not understand it and it is to be reviewed later. Nothing is deleted: set it
 * to `false` to show it all again.
 */
export const UPSELLING_HIDDEN = true;

/**
 * Actions the receiving admin can take on a request sent to them.
 * The backend validates these against a ChoiceField, so the values are exact.
 */
export const UPSELL_ACTIONS = {
  ACCEPT: 'ACCEPT',
  REJECT: 'REJECT',
};

export const UPSELL_ACTION_LABELS = {
  [UPSELL_ACTIONS.ACCEPT]: 'Aceptar',
  [UPSELL_ACTIONS.REJECT]: 'Rechazar',
};

/**
 * `UpsellStatus` as the backend declares it.
 *
 * The inbox serializer does not return the status today (its `Meta` is
 * decorative over a plain `Serializer`), so this is not read by the list yet.
 * It is here because the deliveries listing is expected to start exposing
 * `du.status`, and because these are the only names the enum accepts.
 * `COMPLETED` and `CANCELLED` exist in the enum but nothing writes them.
 */
export const UPSELL_STATUS_LABELS = {
  PENDING: 'Pendiente',
  ACCEPTED: 'Aceptado',
  REJECTED: 'Rechazado',
  CREATED_ON_TMS: 'Creado en TMS',
  FAILED_ON_TMS: 'Fallido en TMS',
  COMPLETED: 'Finalizado',
  CANCELLED: 'Cancelado',
};

/**
 * Colour per upsell status, for the chip the deliveries table paints on a ceded
 * shipment. Keyed by the same names the backend sends in `upsell_status`.
 */
export const UPSELL_STATUS_STYLES = {
  PENDING: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 border-amber-200 dark:border-amber-900/50',
  ACCEPTED: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 border-blue-200 dark:border-blue-900/50',
  CREATED_ON_TMS: 'bg-ninja-greenLight dark:bg-ninja-green/10 text-ninja-green border-ninja-green/30',
  FAILED_ON_TMS: 'bg-red-50 dark:bg-red-900/20 text-red-500 border-red-200 dark:border-red-900/50',
};

/**
 * Filter values the deliveries listing accepts for `upsell_filter`. They mirror
 * `assignedDriverFilter`, which is the vocabulary the backend already parses.
 */
export const UPSELL_FILTER_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'assigned', label: 'Derivados' },
  { value: 'not_assigned', label: 'No derivados' },
];

/**
 * Formats the cost frozen on a ceded shipment. It arrives as a decimal string.
 * @param {string|number|null} value
 * @returns {string}
 */
export const formatAgreedCost = (value) => {
  // Guarded before Number(): it turns null and '' into 0, which would print a
  // real "$0" for an alliance that simply has no cost recorded.
  if (value === null || value === undefined || value === '') return '-';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return amount.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
};

/**
 * Normalizes the answer of `assign/` into one shape.
 *
 * Two things make the raw response awkward to render. The endpoint sends the
 * same useful body under a 200 and under a 400 (the 400 only means no order
 * made it through), and it silently drops orders that belong to another seller
 * or that were ceded before — those never reach `failed_deliveries`, so
 * `total_requested` can exceed `success_count + failed_count`. That difference
 * is surfaced as `skipped` instead of quietly disappearing.
 *
 * @param {object} payload - Response body from `assign/`.
 * @param {number} requestedCount - Orders sent, used when the body omits the count.
 * @returns {{requested: number, succeeded: number, failed: number, skipped: number, failures: Array}}
 */
export const summarizeAssignment = (payload, requestedCount = 0) => {
  const summary = payload?.summary || {};

  const requested = Number.isFinite(Number(summary.total_requested))
    ? Number(summary.total_requested)
    : requestedCount;
  const succeeded = Number(summary.success_count) || 0;
  const failed = Number(summary.failed_count) || 0;

  return {
    requested,
    succeeded,
    failed,
    skipped: Math.max(0, requested - succeeded - failed),
    failures: Array.isArray(payload?.failed_deliveries) ? payload.failed_deliveries : [],
  };
};
